/**
 * Pagination component for meetings list
 */

import {useMeetingsStore} from '@voxapilot/shared';
import {Button} from '../ui/Button';
import {cn} from '../../utils/cn';

interface PaginationProps {
  total: number;
  hasMore: boolean;
  onLoadMore?: () => void;
}

export function Pagination({total, hasMore, onLoadMore}: PaginationProps) {
  const {filters, setFilters} = useMeetingsStore();
  const {limit = 20, offset = 0} = filters;

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const handlePrevious = () => {
    if (offset > 0) {
      setFilters({...filters, offset: Math.max(0, offset - limit)});
    }
  };

  const handleNext = () => {
    if (hasMore) {
      setFilters({...filters, offset: offset + limit});
    }
  };

  const handlePageClick = (page: number) => {
    setFilters({...filters, offset: (page - 1) * limit});
  };

  if (total === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-between mt-6">
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} meetings
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={offset === 0}
        >
          Previous
        </Button>

        {/* Page Numbers */}
        <div className="flex gap-1">
          {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageClick(pageNum)}
                className={cn(
                  'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                  currentPage === pageNum
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={!hasMore}
        >
          Next
        </Button>

        {hasMore && onLoadMore && (
          <Button variant="ghost" size="sm" onClick={onLoadMore}>
            Load More
          </Button>
        )}
      </div>
    </div>
  );
}

